import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const hourMarkers = Array.from({ length: 12 });
const socialLinks = [
  {
    href: "https://facebook.com/",
    icon: (
      <svg
        className="w-5 h-5"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0" />
      </svg>
    ),
  },
  {
    href: "https://linkedin.com/",
    icon: (
      <svg
        className="w-5 h-5"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11.5 19h-3v-9h3v9zm-1.5-10.268c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm13.5 10.268h-3v-4.604c0-1.096-.021-2.507-1.528-2.507-1.529 0-1.764 1.195-1.764 2.427v4.684h-3v-9h2.881v1.233h.041c.401-.761 1.379-1.563 2.841-1.563 3.039 0 3.6 2.001 3.6 4.6v4.73z" />
      </svg>
    ),
  },
  {
    href: "https://tiktok.com/",
    icon: (
      <svg
        className="w-10 h-10"
        fill="currentColor"
        viewBox="6 0 24 24"
      >
        <path
          d="M15.5 7.5c-.28 0-.5-.22-.5-.5V6h-2v8.5a1.5 1.5 0 1 1-1.5-1.5h.5v-1.5h-.5a3 3 0 1 0 3 3V11c.7.5 1.6.8 2.5.8v-1.5c-.5 0-.9-.1-1.3-.3z"
          fill="currentColor"
        />
      </svg>
    ),
  },
];

function getHandAngles(date: Date) {
  const hours = date.getHours() % 12;
  const minutes = date.getMinutes();
  const hourAngle = (hours + minutes / 60) * 30; // 360/12
  const minuteAngle = minutes * 6; // 360/60
  return { hourAngle, minuteAngle };
}

const getClockColor = (isDark: boolean) =>
  isDark ? "#fff" : "#222";
const getMinuteHandColor = (isDark: boolean) =>
  isDark ? "#FE6019" : "#FE6019";

const ComingSoon: React.FC = () => {
  const [time, setTime] = useState(new Date());
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const interval = setInterval(
      () => setTime(new Date()),
      1000
    );
    // Detect dark mode
    const checkDark = () =>
      setIsDark(
        document.documentElement.classList.contains("dark")
      );
    checkDark();
    window.addEventListener("storage", checkDark);
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkDark);
      observer.disconnect();
    };
  }, []);
  const { hourAngle, minuteAngle } = getHandAngles(time);

  return (
    <>
      <Helmet>
        <title>Coming Soon</title>
      </Helmet>
      <div className="relative min-h-screen flex items-center justify-center bg-white dark:bg-black overflow-hidden">
        {/* Background overlay */}
        {/* <div className="absolute inset-0 bg-black bg-opacity-60 z-0 dark:bg-opacity-80" /> */}
        {/* Centered content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full h-full mt-40 gap-5">
          {/* Clock SVG */}
          <div
            className="relative flex items-center justify-center"
            style={{ width: 420, height: 420 }}
          >
            <svg
              width={420}
              height={420}
              className="absolute left-0 top-0"
            >
              {/* Hour markers */}
              {hourMarkers.map((_, i) => {
                const angle = i * 30 * (Math.PI / 180);
                const r1 = 170,
                  r2 = 200,
                  cx = 210,
                  cy = 210;
                const x1 =
                  cx + r1 * Math.cos(angle - Math.PI / 2);
                const y1 =
                  cy + r1 * Math.sin(angle - Math.PI / 2);
                const x2 =
                  cx + r2 * Math.cos(angle - Math.PI / 2);
                const y2 =
                  cy + r2 * Math.sin(angle - Math.PI / 2);
                return (
                  <line
                    key={i}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke={getClockColor(isDark)}
                    strokeWidth={3}
                    strokeLinecap="round"
                    opacity={0.7}
                  />
                );
              })}
              {/* Minute hand */}
              <line
                x1={210}
                y1={210}
                x2={
                  210 +
                  120 *
                    Math.cos(
                      ((minuteAngle - 90) * Math.PI) / 180
                    )
                }
                y2={
                  210 +
                  120 *
                    Math.sin(
                      ((minuteAngle - 90) * Math.PI) / 180
                    )
                }
                stroke={getMinuteHandColor(isDark)}
                strokeWidth={4}
                strokeLinecap="round"
              />
              {/* Hour hand */}
              <line
                x1={210}
                y1={210}
                x2={
                  210 +
                  80 *
                    Math.cos(
                      ((hourAngle - 90) * Math.PI) / 180
                    )
                }
                y2={
                  210 +
                  80 *
                    Math.sin(
                      ((hourAngle - 90) * Math.PI) / 180
                    )
                }
                stroke={getClockColor(isDark)}
                strokeWidth={7}
                strokeLinecap="round"
              />
            </svg>
            {/* Text overlay */}
            <div className="absolute left-0 top-0 w-full h-full flex flex-col items-center justify-center select-none">
              <span
                className={`uppercase tracking-widest text-base font-bold mb-2 drop-shadow-md ${
                  isDark
                    ? "text-[#FE6019]"
                    : "text-blue-700"
                }`}
                style={{ letterSpacing: 4 }}
              >
                OUR NEW SITE IS
              </span>
              <span
                className="text-5xl md:text-6xl font-extrabold tracking-widest mb-2 bg-gradient-to-r from-[#FE6019] to-[#ffb347] bg-clip-text text-transparent drop-shadow-[0_2px_16px_rgba(254,96,25,0.25)]"
                style={{ letterSpacing: 8 }}
              >
                COMING
              </span>
              <span
                className="text-5xl md:text-6xl font-extrabold tracking-widest mb-4 bg-gradient-to-r from-blue-700 to-blue-400 bg-clip-text text-transparent drop-shadow-[0_2px_16px_rgba(30,64,175,0.18)]"
                style={{ letterSpacing: 8 }}
              >
                SOON
              </span>
            </div>
          </div>
          <Link
            to="/"
            className="px-8 py-3 rounded-full bg-black hover:bg-[#FE6019] shadow-lg text-white font-semibold flex items-center gap-2 hover:from-primary hover:to-secondary transition-all duration-200 mb-10 dark:bg-white dark:text-black dark:hover:bg-[#FE6019] dark:hover:text-white"
          >
            Back To Home
            <span className="inline-block ml-2">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 12h14M12 5l7 7-7 7"
                />
              </svg>
            </span>
          </Link>
          {/* Social icons (now absolute, not fixed) */}
          <div className="absolute right-8 top-1/2 transform -translate-y-1/2 z-20 flex flex-col space-y-6">
            {socialLinks.map((s, i) => (
              <a
                key={i}
                href={s.href}
                className="text-gray-900 dark:text-white opacity-70 hover:opacity-100 transition"
                target="_blank"
                rel="noopener noreferrer"
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ComingSoon;
